/**
 * The filters a whole-base question was asked under, above its answer
 * (#765 Ф3). Without them «5 records in August» cannot be read: the same
 * thread may hold answers under different filters only through history the
 * page itself no longer shows (a change of filters opens a new thread), but
 * the drawer reads the same threads with no filter controls at all.
 */

import { useTranslation } from 'react-i18next';
import type { AssistantOrgFilters } from '@aica/api-types/schemas';
import { useAssistantFilterNames } from '@/contexts/AssistantFilterNamesContext';
import { formatDateShort } from '@/lib/utils';

export function AssistantTurnFilters({ filters }: { filters: AssistantOrgFilters }) {
  const { t } = useTranslation(['assistant', 'records']);
  const names = useAssistantFilterNames();

  const parts: string[] = [];
  if (filters.dateFrom || filters.dateTo) {
    parts.push(
      t('assistant:filters.periodValue', {
        from: filters.dateFrom ? formatDateShort(filters.dateFrom) : '…',
        to: filters.dateTo ? formatDateShort(filters.dateTo) : '…',
      })
    );
  }
  for (const id of filters.projectIds ?? []) {
    const name = names?.projects.get(id);
    parts.push(
      name
        ? t('assistant:filters.projectValue', { name })
        : t('assistant:filters.projectUnnamed')
    );
  }
  for (const id of filters.agentIds ?? []) {
    const name = names?.agents.get(id);
    parts.push(
      name
        ? t('assistant:filters.agentValue', { name })
        : t('assistant:filters.agentUnnamed')
    );
  }
  if (filters.type) parts.push(t(`assistant:filters.typeValue.${filters.type}`));
  if (filters.callCategory) {
    parts.push(t(`records:records.callCategory.${filters.callCategory}`));
  }
  if (filters.entityName) {
    parts.push(t('assistant:filters.entityValue', { name: filters.entityName }));
  }
  if (parts.length === 0) return null;

  return (
    <p className="text-xs text-fg-subtle" data-testid="assistant-turn-filters">
      {parts.join(' · ')}
    </p>
  );
}
